import { useState, useEffect, useMemo } from 'react';
import api from '../api';
import {
  Star,
  RefreshCw,
  Search,
  X,
  PlayCircle,
  ExternalLink,
  Award,
  TrendingUp,
  UserCheck,
  ShieldCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function ReviewHub() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [polling, setPolling] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifySubmitting, setVerifySubmitting] = useState(false);
  const [verifyForm, setVerifyForm] = useState({
    userEmail: '',
    reviewerDisplayName: '',
    starRating: 5,
    reviewText: '',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [listRes, statsRes] = await Promise.all([
        api.get('/reviews/admin/list', { params: { limit: 500 } }),
        api.get('/reviews/admin/stats').catch(() => ({ data: {} })),
      ]);
      setReviews(listRes.data.reviews || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error('Error fetching review data:', error);
      toast.error('Failed to load review data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleTriggerPoll = async () => {
    try {
      setPolling(true);
      const { data } = await api.post('/reviews/admin/poll-now');
      if (data.skipped) {
        toast(data.reason ? `Skipped: ${data.reason}` : 'Skipped: nothing pending', { icon: 'ℹ️' });
      } else if (data.error) {
        toast.error(`Poll error: ${data.error}`);
      } else {
        toast.success(
          `Scanned ${data.reviewsScanned ?? 0}, matched ${data.matched ?? 0} (${data.backend})`
        );
        await fetchData();
      }
    } catch (e) {
      toast.error('Failed to trigger poll');
    } finally {
      setPolling(false);
    }
  };

  const openVerifyModal = () => {
    setVerifyForm({
      userEmail: '',
      reviewerDisplayName: '',
      starRating: 5,
      reviewText: '',
    });
    setVerifyOpen(true);
  };

  const handleVerifySubmit = async () => {
    const f = verifyForm;
    if (!f.userEmail.trim() || !f.reviewerDisplayName.trim()) {
      toast.error('User email and reviewer name are required');
      return;
    }
    setVerifySubmitting(true);
    try {
      const { data } = await api.post('/reviews/admin/verify', {
        userEmail: f.userEmail.trim(),
        reviewerDisplayName: f.reviewerDisplayName.trim(),
        starRating: f.starRating,
        reviewText: f.reviewText.trim() || null,
      });
      if (data.alreadyConfirmed) {
        toast(`Already confirmed at ${new Date(data.confirmedAt).toLocaleString()}`, { icon: 'ℹ️' });
      } else {
        toast.success(
          `Verified! ${data.rewardAssignedCount > 0 ? `Triggered ${data.rewardAssignedCount} reward(s).` : ''}`
        );
      }
      setVerifyOpen(false);
      await fetchData();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Verify failed';
      toast.error(msg);
    } finally {
      setVerifySubmitting(false);
    }
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filtered = useMemo(() => {
    if (!searchQuery) return reviews;
    const q = searchQuery.toLowerCase();
    return reviews.filter((r) => {
      const userName = `${r.user?.firstName || ''} ${r.user?.lastName || ''}`
        .trim()
        .toLowerCase();
      const userEmail = (r.user?.email || '').toLowerCase();
      const reviewer = (r.reviewerDisplayName || '').toLowerCase();
      const text = (r.reviewText || '').toLowerCase();
      return (
        userName.includes(q) ||
        userEmail.includes(q) ||
        reviewer.includes(q) ||
        text.includes(q)
      );
    });
  }, [reviews, searchQuery]);

  const formatDate = (s) => {
    if (!s) return '-';
    const d = new Date(s);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const renderStars = (rating) => {
    if (rating == null) {
      return <span className="text-gray-500 text-xs italic">no rating</span>;
    }
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            size={14}
            className={
              n <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-600'
            }
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Star className="text-amber-500" /> Review Hub
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Verified Google reviews left by your users
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openVerifyModal}
            className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
          >
            <UserCheck size={16} />
            Manually Verify
          </button>
          <button
            onClick={handleTriggerPoll}
            disabled={polling}
            className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
          >
            <PlayCircle size={16} />
            {polling ? 'Polling...' : 'Trigger Poll'}
          </button>
          <button
            onClick={fetchData}
            className="bg-amber-500 hover:bg-amber-600 text-black px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-semibold"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-xs uppercase tracking-wide text-gray-400">
              Verified Reviews
            </div>
            <div className="text-3xl font-bold text-white mt-1">
              {stats.total ?? 0}
            </div>
            {stats.lastConfirmedAt && (
              <div className="text-xs text-gray-500 mt-2">
                Last: {formatDate(stats.lastConfirmedAt)}
              </div>
            )}
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-xs uppercase tracking-wide text-gray-400">
              Average Rating
            </div>
            <div className="text-3xl font-bold text-white mt-1 flex items-center gap-2">
              {stats.averageRating != null ? stats.averageRating.toFixed(2) : '-'}
              {stats.averageRating != null && (
                <Star size={20} className="fill-amber-400 text-amber-400" />
              )}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Across all confirmed reviews
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-xs uppercase tracking-wide text-gray-400">
              Rewards Triggered
            </div>
            <div className="text-3xl font-bold text-white mt-1 flex items-center gap-2">
              {stats.rewardsTriggered ?? 0}
              <Award size={20} className="text-amber-500" />
            </div>
            <div className="text-xs text-gray-500 mt-2">
              From review_confirmed triggers
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-xs uppercase tracking-wide text-gray-400">
              Star Distribution
            </div>
            <div className="mt-2 space-y-1">
              {[5, 4, 3, 2, 1].map((n) => {
                const count = stats.ratingBreakdown?.[String(n)] || 0;
                const pct =
                  stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                return (
                  <div key={n} className="flex items-center gap-2 text-xs">
                    <span className="text-gray-300 w-3">{n}</span>
                    <Star
                      size={10}
                      className="fill-amber-400 text-amber-400"
                    />
                    <div className="flex-1 bg-gray-700 rounded-full h-1.5">
                      <div
                        className="bg-amber-500 h-1.5 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-gray-400 w-6 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 flex items-center gap-3">
        <Search size={18} className="text-gray-500" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by user name, email, reviewer name, or review text..."
          className="flex-1 bg-transparent text-white placeholder-gray-500 outline-none"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="text-gray-500 hover:text-white"
          >
            <X size={18} />
          </button>
        )}
        <span className="text-xs text-gray-500">
          {filtered.length} of {reviews.length}
        </span>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center text-gray-400">
          {reviews.length === 0
            ? 'No verified reviews yet. The first one will land here automatically when a user finishes the Leave Review flow.'
            : 'No reviews match this search.'}
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-900 text-xs uppercase text-gray-400">
              <tr>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Reviewer on Google</th>
                <th className="px-4 py-3 text-left">Rating</th>
                <th className="px-4 py-3 text-left">Review</th>
                <th className="px-4 py-3 text-left">Confirmed</th>
                <th className="px-4 py-3 text-left">Rewards</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const userLabel =
                  `${r.user?.firstName || ''} ${r.user?.lastName || ''}`.trim() ||
                  '(unnamed)';
                const text = r.reviewText || '';
                const isExpanded = expanded.has(r.id);
                const truncated =
                  text.length > 140 && !isExpanded ? text.slice(0, 140) + '...' : text;
                return (
                  <tr
                    key={r.id}
                    className="border-t border-gray-700 hover:bg-gray-700/50 align-top"
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm text-white">{userLabel}</div>
                      <div className="text-xs text-gray-500">
                        {r.user?.email}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      <div className="flex items-center gap-1.5">
                        <span>
                          {r.reviewerDisplayName || (
                            <span className="text-gray-500 italic text-xs">unknown</span>
                          )}
                        </span>
                        {r.googleReviewId && r.googleReviewId.startsWith('admin:') && (
                          <span
                            title="Admin-verified (not via Places API)"
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 text-[10px] uppercase tracking-wide"
                          >
                            <ShieldCheck size={10} />
                            admin
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">{renderStars(r.starRating)}</td>
                    <td className="px-4 py-3 text-sm text-gray-300 max-w-md">
                      {text ? (
                        <>
                          <div className="whitespace-pre-wrap">{truncated}</div>
                          {text.length > 140 && (
                            <button
                              onClick={() => toggleExpand(r.id)}
                              className="text-xs text-amber-400 hover:text-amber-300 mt-1"
                            >
                              {isExpanded ? 'Collapse' : 'Show more'}
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-500 italic text-xs">
                          no text
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">
                      {formatDate(r.confirmedAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300 text-center">
                      {r.rewardAssignedCount > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">
                          <Award size={12} />
                          {r.rewardAssignedCount}
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Manual Verify Modal */}
      {verifyOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <UserCheck className="text-amber-500" size={22} />
                <h2 className="text-lg font-semibold text-white">
                  Manually Verify Review
                </h2>
              </div>
              <button
                onClick={() => setVerifyOpen(false)}
                disabled={verifySubmitting}
                className="text-gray-500 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-xs text-gray-400 mb-4">
              Use this when the Places API can&apos;t see a user&apos;s review
              (typically because they edited an old review). Look at the review
              on Google and copy the values here.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase text-gray-400 mb-1">
                  User email
                </label>
                <input
                  type="email"
                  value={verifyForm.userEmail}
                  onChange={(e) =>
                    setVerifyForm({ ...verifyForm, userEmail: e.target.value })
                  }
                  placeholder="user@example.com"
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-xs uppercase text-gray-400 mb-1">
                  Reviewer name on Google
                </label>
                <input
                  type="text"
                  value={verifyForm.reviewerDisplayName}
                  onChange={(e) =>
                    setVerifyForm({ ...verifyForm, reviewerDisplayName: e.target.value })
                  }
                  placeholder="Tyler L."
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-xs uppercase text-gray-400 mb-1">
                  Star rating
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() =>
                        setVerifyForm({ ...verifyForm, starRating: n })
                      }
                      className="p-1"
                      disabled={verifySubmitting}
                    >
                      <Star
                        size={26}
                        className={
                          n <= verifyForm.starRating
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-gray-600'
                        }
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase text-gray-400 mb-1">
                  Review text (optional)
                </label>
                <textarea
                  value={verifyForm.reviewText}
                  onChange={(e) =>
                    setVerifyForm({ ...verifyForm, reviewText: e.target.value })
                  }
                  rows={3}
                  placeholder="Paste the review text from Google..."
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm outline-none focus:border-amber-500 resize-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={() => setVerifyOpen(false)}
                disabled={verifySubmitting}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleVerifySubmit}
                disabled={verifySubmitting}
                className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
              >
                {verifySubmitting ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : (
                  <ShieldCheck size={14} />
                )}
                Verify and trigger reward
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
